import {
  BaseScrollGlobalStyle,
  GlobalColors,
  GlobalFonts,
  ThemeGlobalStyles,
  ThemeOrangeGlobalStyles,
} from '@toggle/design-system';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { appWithTranslation } from 'next-i18next';
import React, { Fragment, useEffect } from 'react';
import { createGlobalStyle } from 'styled-components';

import { Footer } from '@/components/footer/Footer';
import { Header } from '@/components/header/Header';
import { Main } from '@/components/main/Main';
import { MediaQueryStore } from '@/stores/MediaQueryStore';
import { routes } from '@/utils/page-routes';
import { setReferralPromoCookies } from '@/utils/set-referral-promo-cookies';

export const GlobalStyles = createGlobalStyle`
  *,
  ::before,
  ::after {
    box-sizing: border-box;
  }

  body {
    margin: 0;
  }

  ul {
    padding: 0;
    margin: 0;
  }

  h1,
  h2,
  h3,
  h4,
  p {
    margin: 0;
  }

  input, button {
    font:inherit;
  }

  html,
  body {
    background-color: var(--background-default);
    color: var(--text-default);
    font-family: var(--font-nort);
    height: 100%;
    margin: 0;
  }

  div#__next {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`;

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter();

  useEffect(() => {
    if (router.query) {
      const { ref, promo } = router.query;
      const referralCode = Array.isArray(ref) ? ref.at(-1) : ref;
      const promoCode = Array.isArray(promo) ? promo.at(-1) : promo;

      setReferralPromoCookies({
        referralCode,
        promoCode,
      });
    }

    router.beforePopState(({ as }) => {
      if (!as.startsWith('/earnings')) {
        location.reload();
      }
      return true;
    });

    return () => {
      router.beforePopState(() => true);
    };
  }, [router]);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no"
        />
      </Head>

      <GlobalFonts />
      <GlobalColors />
      <GlobalStyles />
      <ThemeGlobalStyles />
      <ThemeOrangeGlobalStyles />
      <BaseScrollGlobalStyle />
      <MediaQueryStore>
        {router.pathname === routes.download ? (
          <Component {...pageProps} />
        ) : (
          <Fragment>
            {!router.asPath.includes(routes.layoutSimple) && (
              <Header {...pageProps} is404={router.pathname === '/404'} />
            )}
            <Main>
              <Component {...pageProps} />
            </Main>
            {!router.asPath.includes(routes.layoutSimple) && <Footer />}
          </Fragment>
        )}
      </MediaQueryStore>
    </>
  );
};

export default appWithTranslation(App);
